import React, { useState, useMemo, useCallback } from 'react';
import { DeleteModal } from '../../../../../components';
import { StructuralSelector } from './StructuralSelector';
import { AttachmentRailSelector } from './AttachmentRailSelector';
import { RoofConfigurationProps } from './RoofConfigurationWithProps';
import { FormInput } from '../../../../../components/shared/inputs';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { SystemKey } from '../../../../../constants';
import { updateStructureData } from '../../../../../store/slices/createCRSlice';
import { Rail, PanelOrientation } from '../../../../../types';
import { DomesticStatus } from '../../../../../types/api.types';
import { AuroraPanelPoolRow } from '../../../../../utils/aurora-mapper.utils';
import {
  selectFramingSizes,
  selectFramingTypes,
  selectRafterSpacings,
  selectAttachments,
  selectRoofTypes,
  selectRoofConditions,
  selectReroofStatuses,
  selectBuildingHeights
} from '../../../../../store/selectors/databaseSelectors';
import {
  selectStructure1Data,
  selectStructure2Data,
  selectStructure3Data,
  selectConfiguredSystems,
  selectAllSystemsModuleData,
  selectAuroraPanelRowsPool,
  selectStructuresRoofsDetails,
} from '../../../../../store/selectors/createCRSelectors';

interface StructuralMultipleSelectorProps {
  isEnabled: boolean;
}

export const StructuralMultipleSelector: React.FC<StructuralMultipleSelectorProps> = ({ isEnabled }) => {
  const dispatch = useAppDispatch();
  const [deleteConfirmStructure, setDeleteConfirmStructure] = useState<number | null>(null);

  // Redux selectors
  const structure1Data = useAppSelector(selectStructure1Data);
  const structure2Data = useAppSelector(selectStructure2Data);
  const structure3Data = useAppSelector(selectStructure3Data);
  const { is_initialized } = useAppSelector((state) => state.database);

  // Database selectors
  const framingSizes = useAppSelector(selectFramingSizes);
  const framingTypes = useAppSelector(selectFramingTypes);
  const rafterSpacings = useAppSelector(selectRafterSpacings);
  const attachments = useAppSelector(selectAttachments);
  const roofTypes = useAppSelector(selectRoofTypes);
  const roofConditions = useAppSelector(selectRoofConditions);
  const reroofStatuses = useAppSelector(selectReroofStatuses);
  const buildingHeights = useAppSelector(selectBuildingHeights);

  // Multi-system module data for panel rows configuration
  const configuredSystems = useAppSelector(selectConfiguredSystems);
  const allSystemsModuleData = useAppSelector(selectAllSystemsModuleData);

  const structuresData = [structure1Data, structure2Data, structure3Data];

  // Total module quantity across ALL configured systems
  const totalModuleQuantity = useMemo(() =>
    configuredSystems.reduce((sum, key) => sum + (allSystemsModuleData[key]?.quantity || 0), 0),
    [configuredSystems, allSystemsModuleData],
  );

  // Global assigned panels across ALL structures and ALL rails
  const globalTotalAssignedPanels = useMemo(() => {
    let total = 0;
    structuresData.forEach(structure => {
      if (structure.attachments_rails?.panel_rows_1?.rows) {
        total += structure.attachments_rails.panel_rows_1.rows.reduce((sum, row) => sum + (row.panel_count || 0), 0);
      }
      if (structure.attachments_rails?.panel_rows_2?.rows) {
        total += structure.attachments_rails.panel_rows_2.rows.reduce((sum, row) => sum + (row.panel_count || 0), 0);
      }
    });
    return total;
  }, [structure1Data, structure2Data, structure3Data]);

  // Per-system assigned panels across ALL structures and ALL rails
  const perSystemAssigned = useMemo(() => {
    const assigned: Partial<Record<SystemKey, number>> = {};
    structuresData.forEach(structure => {
      const allRows = [
        ...(structure.attachments_rails?.panel_rows_1?.rows || []),
        ...(structure.attachments_rails?.panel_rows_2?.rows || []),
      ];
      allRows.forEach(row => {
        const sk = (row as any).system_key as SystemKey | undefined ?? 'system_one';
        assigned[sk] = (assigned[sk] || 0) + (row.panel_count || 0);
      });
    });
    return assigned;
  }, [structure1Data, structure2Data, structure3Data]);

  const globalRemainingPanels = totalModuleQuantity - globalTotalAssignedPanels;

  // Aurora panel rows pool and structure-system assignments
  const auroraPanelRowsPool = useAppSelector(selectAuroraPanelRowsPool);
  const structuresRoofsDetails = useAppSelector(selectStructuresRoofsDetails);
  const hasAuroraPool = auroraPanelRowsPool !== null;

  // Collect all assigned row IDs across ALL structures — used to derive available pool rows
  const allAssignedRowIds = useMemo(() => {
    const ids = new Set<string>();
    structuresData.forEach(s => {
      (s?.attachments_rails?.panel_rows_1?.rows || []).forEach((r: Record<string, unknown>) => ids.add(r.id as string));
      (s?.attachments_rails?.panel_rows_2?.rows || []).forEach((r: Record<string, unknown>) => ids.add(r.id as string));
    });
    return ids;
  }, [structure1Data, structure2Data, structure3Data]);

  // Available pool = master pool minus already-assigned rows (pool is never mutated)
  const availablePoolRows = useMemo(() =>
    auroraPanelRowsPool?.filter(r => !allAssignedRowIds.has(r.id)) ?? null,
    [auroraPanelRowsPool, allAssignedRowIds],
  );

  // Assign a pool row to a structure's rail panel_rows
  const handleAssignPoolRow = useCallback((structureNumber: number, railNumber: 1 | 2, rowId: string) => {
    if (!auroraPanelRowsPool) return;
    const poolRow = auroraPanelRowsPool.find(r => r.id === rowId);
    if (!poolRow) return;

    const structData = structuresData[structureNumber - 1];
    const panelRowsKey = railNumber === 1 ? 'panel_rows_1' : 'panel_rows_2';
    const existingRows = structData?.attachments_rails?.[panelRowsKey]?.rows || [];
    dispatch(updateStructureData({
      structureNumber: structureNumber as 1 | 2 | 3,
      data: {
        attachments_rails: {
          [panelRowsKey]: {
            rows: [...existingRows, {
              id: poolRow.id,
              orientation: poolRow.orientation,
              panel_count: poolRow.panel_count,
            }],
          },
        },
      },
    }));
  }, [auroraPanelRowsPool, structuresData, dispatch]);

  // Unassign a row from a structure's rail
  const handleUnassignPoolRow = useCallback((structureNumber: number, railNumber: 1 | 2, rowId: string) => {
    const structData = structuresData[structureNumber - 1];
    const panelRowsKey = railNumber === 1 ? 'panel_rows_1' : 'panel_rows_2';
    const existingRows = structData?.attachments_rails?.[panelRowsKey]?.rows || [];
    dispatch(updateStructureData({
      structureNumber: structureNumber as 1 | 2 | 3,
      data: {
        attachments_rails: {
          [panelRowsKey]: {
            rows: existingRows.filter((r: any) => r.id !== rowId),
          },
        },
      },
    }));
  }, [structuresData, dispatch]);

  // Initialize enabledStructures based on existing data in Redux
  const [enabledStructures, setEnabledStructures] = useState<number[]>(() => {
    const enabled: number[] = [];
    
    structuresData.forEach((structureData, index) => {
      // Check if this structure has any meaningful data
      if (structureData?.name || 
          structureData?.framing?.size || 
          structureData?.framing?.type || 
          structureData?.roof_configuration?.roof_type ||
          structureData?.attachments_rails?.attachment_1) {
        enabled.push(index + 1);
      }
    });
    
    // If no structures have data, start with structure 1 enabled
    return enabled.length > 0 ? enabled : [1];
  });

  // Initialize expandedStructures to match enabledStructures
  const [expandedStructures, setExpandedStructures] = useState<number[]>(() => {
    const enabled: number[] = [];
    
    structuresData.forEach((structureData, index) => {
      // Check if this structure has any meaningful data
      if (structureData?.name || 
          structureData?.framing?.size || 
          structureData?.framing?.type || 
          structureData?.roof_configuration?.roof_type ||
          structureData?.attachments_rails?.attachment_1) {
        enabled.push(index + 1);
      }
    });
    
    // If no structures have data, start with structure 1 expanded
    return enabled.length > 0 ? enabled : [1];
  });

  // Aurora info bar: group pool + assigned rows by structure for display
  const auroraInfoData = useMemo(() => {
    if (!hasAuroraPool) return null;

    // Collect all assigned rows across all structures and rails
    const assignedRows: { row: Record<string, unknown>; structureNumber: number; railNumber: number }[] = [];
    structuresData.forEach((structData, idx) => {
      (structData?.attachments_rails?.panel_rows_1?.rows || []).forEach((r: Record<string, unknown>) => {
        assignedRows.push({ row: r, structureNumber: idx + 1, railNumber: 1 });
      });
      (structData?.attachments_rails?.panel_rows_2?.rows || []).forEach((r: Record<string, unknown>) => {
        assignedRows.push({ row: r, structureNumber: idx + 1, railNumber: 2 });
      });
    });

    const pool = availablePoolRows || [];
    const allRows = [
      ...pool.map(r => ({ ...r, assigned: false as const, assignedStructure: 0, railNumber: 0 })),
      ...assignedRows.map(({ row, structureNumber, railNumber }) => ({
        id: row.id as string,
        orientation: row.orientation as PanelOrientation,
        panel_count: (row.panel_count as number) || 0,
        structure_index: structureNumber,
        assigned: true as const,
        assignedStructure: structureNumber,
        railNumber,
      })),
    ];

    // Group by structure
    const byStructure: Record<number, typeof allRows> = {};
    allRows.forEach(r => {
      const key = r.assigned ? r.assignedStructure : r.structure_index;
      if (!byStructure[key]) byStructure[key] = [];
      byStructure[key].push(r);
    });

    const totalPanels = allRows.reduce((s, r) => s + r.panel_count, 0);
    const assignedPanels = allRows.filter(r => r.assigned).reduce((s, r) => s + r.panel_count, 0);

    return { byStructure, totalPanels, assignedPanels };
  }, [auroraPanelRowsPool, structuresData, structure1Data, structure2Data, structure3Data]);

  const handleAddStructure = () => {
    const nextStructureNumber = [1, 2, 3].find(num => !enabledStructures.includes(num));
    if (nextStructureNumber) {
      setEnabledStructures([...enabledStructures, nextStructureNumber].sort((a, b) => a - b));
      setExpandedStructures([...expandedStructures, nextStructureNumber]);
    }
  };

  const handleDeleteStructure = (structureNumber: number) => {
    setDeleteConfirmStructure(structureNumber);
  };

  const confirmDeleteStructure = () => {
    if (deleteConfirmStructure) {
      // Clear the structure data
      dispatch(updateStructureData({
        structureNumber: deleteConfirmStructure as 1 | 2 | 3,
        data: {
          name: undefined,
          framing: undefined,
          attachments_rails: undefined,
          roof_configuration: undefined
        }
      }));
      
      // Remove from enabled structures list
      setEnabledStructures(enabledStructures.filter(num => num !== deleteConfirmStructure));
      setExpandedStructures(expandedStructures.filter(num => num !== deleteConfirmStructure));
      setDeleteConfirmStructure(null);
    }
  };

  const toggleStructureExpanded = (structureNumber: number) => {
    if (expandedStructures.includes(structureNumber)) {
      setExpandedStructures(expandedStructures.filter(num => num !== structureNumber));
    } else {
      setExpandedStructures([...expandedStructures, structureNumber]);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-rose-400 overflow-hidden">
      {/* Card Header */}
      <div className="px-5 py-3 3xl:px-6 3xl:py-3.5 bg-rose-100/50 border-b border-slate-200/60">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Structures</span>
            </div>
            <div className="flex items-center gap-2 px-2.5 py-0.5 bg-rose-100/60 text-rose-700 rounded-full border border-rose-200/50">
              <span className="text-[10px] 3xl:text-xs font-semibold">{enabledStructures.length}/3 configured</span>
            </div>
          </div>

          {enabledStructures.length < 3 && (
            <button
              type="button"
              onClick={handleAddStructure}
              className="text-[10px] 3xl:text-xs py-1 px-3 font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-full shadow-sm transition-colors duration-200"
            >
              + Add Structure
            </button>
          )}
        </div>
        <p className="text-[10px] 3xl:text-xs text-slate-400 mt-0.5 ml-6">Framing, roof, attachments & panel rows per structure</p>
      </div>

      <div className="p-5 space-y-4">

      {/* Aurora Panel Rows Info Bar */}
      {auroraInfoData && (
        <div className="bg-slate-50/80 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <span className="text-[11px] 3xl:text-xs uppercase tracking-[0.15em] font-bold text-slate-600">Aurora Panel Rows</span>
            </div>
            <span className={`text-[10px] 3xl:text-xs font-semibold ${
              auroraInfoData.assignedPanels === auroraInfoData.totalPanels ? 'text-emerald-600' : 'text-amber-600'
            }`}>
              Assigned: {auroraInfoData.assignedPanels} / {auroraInfoData.totalPanels} panels
            </span>
          </div>

          <div className="space-y-2">
            {Object.entries(auroraInfoData.byStructure)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([structIdx, rows]) => {
              const structNum = Number(structIdx);
              const structName = structuresRoofsDetails?.[structNum - 1]?.structure_name ?? `Structure ${structNum}`;
              const structPanels = rows.reduce((s, r) => s + r.panel_count, 0);

              return (
                <div key={structNum} className="bg-white rounded-lg p-2.5 border border-slate-200">
                  <div className="flex items-center gap-2 flex-wrap">
                    <svg className="w-3 h-3 text-violet-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10l9-7 9 7M4 10v10h6v-6h4v6h6V10" />
                    </svg>
                    <span className="text-[11px] 3xl:text-xs font-semibold text-slate-800">{structName}</span>
                    <span className="text-[10px] 3xl:text-[11px] font-medium text-violet-700">{structPanels} panels</span>
                    {rows.map((r, idx) => (
                      <div
                        key={r.id}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] 3xl:text-[11px] font-medium ${
                          r.assigned
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-amber-50 text-amber-800 border border-amber-200'
                        }`}
                      >
                        <span>{idx + 1}.</span>
                        <span>{r.orientation === PanelOrientation.LANDSCAPE ? 'L' : 'P'}</span>
                        <span className="font-bold">{r.panel_count}</span>
                        {r.assigned ? (
                          <span className="text-emerald-600">R{r.railNumber}</span>
                        ) : (
                          <span className="text-amber-600">Available</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Multiple Structure Configurations */}
      <div className="space-y-4">
        {enabledStructures.map((structureNumber) => {
          const structureData = structuresData[structureNumber - 1];
          const isExpanded = expandedStructures.includes(structureNumber);

          return (
            <div key={structureNumber} className="border border-slate-200/80 border-l-[3px] border-l-rose-300 rounded-lg shadow-sm bg-white">
              {/* Structure Header */}
              <div className="bg-rose-50/30 px-4 py-3 flex items-center justify-between border-b border-slate-200/60">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleStructureExpanded(structureNumber)}
                    className="text-rose-400 hover:text-rose-600 transition-colors"
                  >
                    <svg 
                      className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <h4 className="text-sm font-semibold text-slate-700">
                    Structure {structureNumber}
                  </h4>
                  {structureData.name && (
                    <span className="text-xs 3xl:text-sm text-slate-500">- {structureData.name}</span>
                  )}
                </div>

                {structureNumber > 1 && (
                  <button
                    onClick={() => handleDeleteStructure(structureNumber)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                    title="Delete Structure"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Structure Content */}
              {isExpanded && (
                <div className="p-4 space-y-6">
                  {/* Structure Name */}
                  <FormInput
                    label="Structure Name"
                    value={structureData.name || ''}
                    onChange={(value) => dispatch(updateStructureData({
                      structureNumber: structureNumber as 1 | 2 | 3,
                      data: { name: value }
                    }))}
                    placeholder="Enter structure name..."
                    disabled={!isEnabled}
                    inputClassName="focus:ring-blue-200 focus:border-blue-300"
                  />

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Structural Configuration */}
                    <StructuralSelector
                        selectedFramingSize={structureData.framing?.size}
                        selectedFramingType={structureData.framing?.type}
                        selectedRafterSpacing={structureData.framing?.rafter_spacing}
                        isAssumed={structureData.framing?.is_assumed || false}
                        framingSizes={framingSizes}
                        framingTypes={framingTypes}
                        rafterSpacings={rafterSpacings}
                        isLoading={!is_initialized}
                        error={undefined}
                        onFramingSizeSelect={(value) => dispatch(updateStructureData({
                          structureNumber: structureNumber as 1 | 2 | 3,
                          data: { 
                            framing: { 
                              is_assumed: false,
                              ...structureData.framing,
                              size: value 
                            } 
                          }
                        }))}
                        onFramingTypeSelect={(value) => dispatch(updateStructureData({
                          structureNumber: structureNumber as 1 | 2 | 3,
                          data: { 
                            framing: { 
                              is_assumed: false,
                              ...structureData.framing,
                              type: value 
                            } 
                          }
                        }))}
                        onRafterSpacingSelect={(value) => dispatch(updateStructureData({
                          structureNumber: structureNumber as 1 | 2 | 3,
                          data: { 
                            framing: { 
                              is_assumed: false,
                              ...structureData.framing,
                              rafter_spacing: value 
                            } 
                          }
                        }))}
                        onAssumedChange={(assumed) => dispatch(updateStructureData({
                          structureNumber: structureNumber as 1 | 2 | 3,
                          data: { 
                            framing: { 
                              ...structureData.framing,
                              is_assumed: assumed 
                            } 
                          }
                        }))}
                        isEnabled={isEnabled}
                      />

                    {/* Roof Configuration */}
                    <div className="h-full">
                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200/70">
                          <svg className="w-3.5 h-3.5 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10l9-7 9 7M4 10v10h6v-6h4v6h6V10" />
                          </svg>
                          <span className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-bold text-slate-600">Roof Configuration</span>
                        </div>

                        <RoofConfigurationProps
                          roofType={structureData.roof_configuration?.roof_type}
                          roofCondition={structureData.roof_configuration?.roof_condition}
                          reroofStatus={structureData.roof_configuration?.reroof_status}
                          buildingHeight={structureData.roof_configuration?.building_height}
                          onRoofTypeChange={(roofType) => dispatch(updateStructureData({
                            structureNumber: structureNumber as 1 | 2 | 3,
                            data: { 
                              roof_configuration: { 
                                ...structureData.roof_configuration,
                                roof_type: roofType?.type 
                              } 
                            }
                          }))}
                          onRoofConditionChange={(roofCondition) => dispatch(updateStructureData({
                            structureNumber: structureNumber as 1 | 2 | 3,
                            data: { 
                              roof_configuration: { 
                                ...structureData.roof_configuration,
                                roof_condition: roofCondition?.value 
                              } 
                            }
                          }))}
                          onReroofStatusChange={(reroofStatus) => dispatch(updateStructureData({
                            structureNumber: structureNumber as 1 | 2 | 3,
                            data: { 
                              roof_configuration: { 
                                ...structureData.roof_configuration,
                                reroof_status: reroofStatus?.value 
                              } 
                            }
                          }))}
                          onBuildingHeightChange={(buildingHeight) => dispatch(updateStructureData({
                            structureNumber: structureNumber as 1 | 2 | 3,
                            data: { 
                              roof_configuration: { 
                                ...structureData.roof_configuration,
                                building_height: buildingHeight?.value 
                              } 
                            }
                          }))}
                          roofTypes={roofTypes}
                          roofConditions={roofConditions}
                          reroofStatuses={reroofStatuses}
                          buildingHeights={buildingHeights}
                          isLoading={!is_initialized}
                          isEnabled={isEnabled}
                        />
                    </div>
                  </div>

                  {/* Attachments & Rails */}
                  <AttachmentRailSelector
                      selectedAttachment={structureData.attachments_rails?.attachment_1 ? attachments.find(a => a.name === structureData.attachments_rails?.attachment_1) : undefined}
                      selectedRail={structureData.attachments_rails?.rail_1 ? {
                        id: structureData.attachments_rails.rail_1.name || '',
                        name: structureData.attachments_rails.rail_1.name || '',
                        domestic_status: structureData.attachments_rails.rail_1.domestic_status || DomesticStatus.DOM,
                        is_domestic: structureData.attachments_rails.rail_1.is_domestic ?? ((structureData.attachments_rails.rail_1.domestic_status || DomesticStatus.DOM) === DomesticStatus.DOM),
                        length: structureData.attachments_rails.rail_1.length,
                        status: 'active' as any,
                        createdAt: '',
                        updatedAt: ''
                      } as Rail : undefined}
                      onAttachmentChange={(attachment) => dispatch(updateStructureData({
                        structureNumber: structureNumber as 1 | 2 | 3,
                        data: {
                          attachments_rails: {
                            ...structureData.attachments_rails,
                            attachment_1: attachment?.name
                          }
                        }
                      }))}
                      onRailChange={(rail) => dispatch(updateStructureData({
                        structureNumber: structureNumber as 1 | 2 | 3,
                        data: {
                          attachments_rails: {
                            ...structureData.attachments_rails,
                            rail_1: rail ? {
                              name: rail.name,
                              domestic_status: rail.domestic_status || (rail as any).domesticStatus,
                              is_domestic: (rail.domestic_status || (rail as any).domesticStatus) === DomesticStatus.DOM,
                              length: rail.length
                            } : undefined,
                            ...((!rail || rail.name !== structureData.attachments_rails?.rail_1?.name) && { panel_rows_1: { rows: [] } })
                          }
                        }
                      }))}
                      selectedAttachment2={structureData.attachments_rails?.attachment_2 ? attachments.find(a => a.name === structureData.attachments_rails?.attachment_2) : undefined}
                      selectedRail2={structureData.attachments_rails?.rail_2 ? {
                        id: structureData.attachments_rails.rail_2.name || '',
                        name: structureData.attachments_rails.rail_2.name || '',
                        domestic_status: structureData.attachments_rails.rail_2.domestic_status || DomesticStatus.DOM,
                        is_domestic: structureData.attachments_rails.rail_2.is_domestic ?? ((structureData.attachments_rails.rail_2.domestic_status || DomesticStatus.DOM) === DomesticStatus.DOM),
                        length: structureData.attachments_rails.rail_2.length,
                        status: 'active' as any,
                        createdAt: '',
                        updatedAt: ''
                      } as Rail : undefined}
                      onAttachment2Change={(attachment) => dispatch(updateStructureData({
                        structureNumber: structureNumber as 1 | 2 | 3,
                        data: {
                          attachments_rails: {
                            ...structureData.attachments_rails,
                            attachment_2: attachment?.name
                          }
                        }
                      }))}
                      onRail2Change={(rail) => dispatch(updateStructureData({
                        structureNumber: structureNumber as 1 | 2 | 3,
                        data: {
                          attachments_rails: {
                            ...structureData.attachments_rails,
                            rail_2: rail ? {
                              name: rail.name,
                              domestic_status: rail.domestic_status || (rail as any).domesticStatus,
                              is_domestic: (rail.domestic_status || (rail as any).domesticStatus) === DomesticStatus.DOM,
                              length: rail.length
                            } : undefined,
                            ...((!rail || rail.name !== structureData.attachments_rails?.rail_2?.name) && { panel_rows_2: { rows: [] } })
                          }
                        }
                      }))}
                      showSecondAttachmentRail={structureData.attachments_rails?.attachment_2 !== undefined}
                      onToggleSecondAttachmentRail={() => {
                        const hasAttachment2 = structureData.attachments_rails?.attachment_2 !== undefined;
                        dispatch(updateStructureData({
                          structureNumber: structureNumber as 1 | 2 | 3,
                          data: {
                            attachments_rails: {
                              ...structureData.attachments_rails,
                              attachment_2: hasAttachment2 ? undefined : '',
                              rail_2: hasAttachment2 ? undefined : { name: '', domestic_status: DomesticStatus.DOM, is_domestic: true },
                              // Clear panel rows when removing second attachment/rail
                              ...(hasAttachment2 && { panel_rows_2: { rows: [] } })
                            }
                          }
                        }))
                      }}
                      attachments={attachments}
                      isLoading={!is_initialized}
                      error={undefined}
                      isEnabled={isEnabled}
                      panelRows1={structureData.attachments_rails?.panel_rows_1?.rows || []}
                      onPanelRows1Change={(rows) => dispatch(updateStructureData({
                        structureNumber: structureNumber as 1 | 2 | 3,
                        data: {
                          attachments_rails: {
                            ...structureData.attachments_rails,
                            panel_rows_1: { rows }
                          }
                        }
                      }))}
                      panelRows2={structureData.attachments_rails?.panel_rows_2?.rows || []}
                      onPanelRows2Change={(rows) => dispatch(updateStructureData({
                        structureNumber: structureNumber as 1 | 2 | 3,
                        data: {
                          attachments_rails: {
                            ...structureData.attachments_rails,
                            panel_rows_2: { rows }
                          }
                        }
                      }))}
                      totalModuleQuantity={totalModuleQuantity}
                      globalTotalAssigned={globalTotalAssignedPanels}
                      globalRemainingPanels={globalRemainingPanels}
                      configuredSystems={configuredSystems}
                      allSystemsModuleData={allSystemsModuleData}
                      perSystemAssigned={perSystemAssigned}
                      auroraPanelRowsPool={availablePoolRows}
                      structureNumber={structureNumber}
                      structuresRoofsDetails={structuresRoofsDetails}
                      onAssignPoolRow={(rowId) => handleAssignPoolRow(structureNumber, 1, rowId)}
                      onUnassignPoolRow={(rowId) => handleUnassignPoolRow(structureNumber, 1, rowId)}
                      onAssignPoolRow2={(rowId) => handleAssignPoolRow(structureNumber, 2, rowId)}
                      onUnassignPoolRow2={(rowId) => handleUnassignPoolRow(structureNumber, 2, rowId)}
                    />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={!!deleteConfirmStructure}
        onClose={() => setDeleteConfirmStructure(null)}
        onConfirm={confirmDeleteStructure}
        title="Delete Structure"
        message={`Are you sure you want to delete Structure ${deleteConfirmStructure}? This action cannot be undone and all configuration data for this structure will be permanently removed.`}
        itemType="Structure"
      />
      </div>
    </div>
  );
};
